import {makeAutoObservable, runInAction, toJS} from "mobx";
import {IInputValitationResponse} from "../../../hooks/input-validation-hook";
import EmailSettingService, {BCC_RECEIVER_ENUM, IEmailSetting} from "../../../services/api/email-setting-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import {IChangeable, ISaveable, WithOld} from "../../../stores/types";


interface IEmailSettingChange extends Partial<IEmailSetting> {
}



class EmailSettingStore implements IChangeable<IEmailSetting>, ISaveable {
	private _isLoading: boolean = false;
	private _isSaving: boolean = false;
	private _model?: IEmailSetting;
	private _changes: IEmailSettingChange = {};
	
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	public get isLoading(): boolean {return this._isLoading;	}
	public get bccEmail(): string {	return this._changes?.bcc_email ?? this._model?.bcc_email ?? '';}
	public get bccOwner(): boolean {return this._changes?.bcc_owner ?? this._model?.bcc_owner ?? false;	}
	private get _shipmentStatusInTransitUpdate(): string { return this._changes?.shipment_status_in_transit_update ?? this._model?.shipment_status_in_transit_update ?? '';}
	private get _shipmentStatusShipmentReceivedUpdate(): string {	return this._changes?.shipment_status_shipment_received_update ?? this._model?.shipment_status_shipment_received_update ?? '';}

	
	public setBccEmail(value:string ) { 
		this._changes.bcc_email = value; 
		this._clearUnneededChanges();
	}
	
	public setBccOwner(value:boolean ){ 
		this._changes.bcc_owner = value; 
		this._clearUnneededChanges();
	}

	public getShipmentStatusInTransitUpdate(target: BCC_RECEIVER_ENUM.MERCHANT | BCC_RECEIVER_ENUM.CUSTOMER){
		return target === this._shipmentStatusInTransitUpdate || this._shipmentStatusInTransitUpdate === BCC_RECEIVER_ENUM.BOTH;
	}
	public toggleShipmentStatusInTransitUpdateByReceiver(target: BCC_RECEIVER_ENUM.MERCHANT | BCC_RECEIVER_ENUM.CUSTOMER){
		//off, set to target or set both if the other is set
		if(!this.getShipmentStatusInTransitUpdate(target)){
			if(this._shipmentStatusInTransitUpdate === BCC_RECEIVER_ENUM.NONE){
				this._setShipmentStatusInTransitUpdate(target);
			}
			else {
				this._setShipmentStatusInTransitUpdate(BCC_RECEIVER_ENUM.BOTH);
			}
		}
		//on, remove it
		else {
			if(this._shipmentStatusInTransitUpdate === BCC_RECEIVER_ENUM.BOTH){
				//keep the other one
				this._setShipmentStatusInTransitUpdate(target === BCC_RECEIVER_ENUM.CUSTOMER ? BCC_RECEIVER_ENUM.MERCHANT : BCC_RECEIVER_ENUM.CUSTOMER);
			}
			else {
				this._setShipmentStatusInTransitUpdate(BCC_RECEIVER_ENUM.NONE);
			}
		}
	}
	
	private _setShipmentStatusInTransitUpdate(value: BCC_RECEIVER_ENUM ) {
		this._changes.shipment_status_in_transit_update = value; 
		this._clearUnneededChanges();
	}
	
	public getShipmentStatusShipmentReceivedUpdateByReceiver(target: BCC_RECEIVER_ENUM.MERCHANT | BCC_RECEIVER_ENUM.CUSTOMER){
		return target === this._shipmentStatusShipmentReceivedUpdate || this._shipmentStatusShipmentReceivedUpdate === BCC_RECEIVER_ENUM.BOTH;
	}
	public toggleShipmentStatusShipmentReceivedByReceiver(target: BCC_RECEIVER_ENUM.MERCHANT | BCC_RECEIVER_ENUM.CUSTOMER){
		//off, set to target or set both if the other is set
		if(!this.getShipmentStatusShipmentReceivedUpdateByReceiver(target)){
			if(this._shipmentStatusShipmentReceivedUpdate === BCC_RECEIVER_ENUM.NONE){
				this._setShipmentStatusShipmentReceivedUpdate(target);
			}
			else {
				this._setShipmentStatusShipmentReceivedUpdate(BCC_RECEIVER_ENUM.BOTH);
			}
		}
		//on, remove it
		else {
			if(this._shipmentStatusShipmentReceivedUpdate === BCC_RECEIVER_ENUM.BOTH){
				//keep the other one
				this._setShipmentStatusShipmentReceivedUpdate(target === BCC_RECEIVER_ENUM.CUSTOMER ? BCC_RECEIVER_ENUM.MERCHANT : BCC_RECEIVER_ENUM.CUSTOMER);
			}
			else {
				this._setShipmentStatusShipmentReceivedUpdate(BCC_RECEIVER_ENUM.NONE);
			}
		}
	}
	private _setShipmentStatusShipmentReceivedUpdate(value: BCC_RECEIVER_ENUM ) {
		this._changes.shipment_status_shipment_received_update = value; 
		this._clearUnneededChanges();
	}

	public isSaving(): boolean {return this._isSaving;	}
	
	public hasChanges(): boolean {
		return !!Object.keys(this._changes).length;
	}

	private get getEmailSettingService(): EmailSettingService {	return rootContainer.get<EmailSettingService>(TYPE.EmailSettingService);}
	
	async load() {
		this._isLoading = true;
		let data = await this.getEmailSettingService.getEmailSetting();
		runInAction(() => {
			this._isLoading = false;
			this._model = ensureValidModelCopy(data);
		});
	}
	
	async save() {
		this._isSaving = true;
        const updatedModel: WithOld<IEmailSetting> = this.getUpdatedModel();
        updatedModel.__old = toJS(this._model);

		const model = await this.getEmailSettingService.updateEmailSetting(updatedModel);
		runInAction(() => {
			this._isSaving = false;
			this.discardChanges();
			this._model = model;
		});
	}
	
    discardChanges(){
        this._changes = {};
    }
	
    public getUpdatedModel(): IEmailSetting {
	    return { ...this._model, ...this._changes } as IEmailSetting;
    }

	private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IEmailSetting;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
    
    public validateBccEmail(): IInputValitationResponse {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.\-_]+\.[A-Z]{2,}$/i;
        if (!this.bccEmail) {
            return  ({isValid: false, messages: ['Email is required']});
        }
        if (!emailRegex.test(this.bccEmail)) {
            return  ({isValid: false, messages: ['Invalid email format']});
        }
        return ({isValid: true, messages: []});
    }
}

function ensureValidModelCopy(data: IEmailSetting|null): IEmailSetting|undefined {
    const toBool = (v: undefined | any) => Boolean(v);

	if (!data) {
		return undefined;
	}

    return {
        ...data,
        bcc_owner: toBool(data.bcc_owner),
    };
}


export default new EmailSettingStore();